import React, { useCallback, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import { CabButton } from '@CabComponents/CabButton';
import { CabModal } from '@CabComponents/CabModal';
import colors from '../../colors';


type TextConfig = {
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
};

type ConfirmationConfig = TextConfig & {
  isDestructive?: boolean;
  doubleConfirm?: TextConfig;
};


// Custom hook for confirmation modal
export const useConfirmationModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<ConfirmationConfig>({
    title: "Confirm Action",
    message: "Are you sure you want to proceed?",
    confirmText: "Confirm",
    cancelText: "Cancel"
  });
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((config: ConfirmationConfig): Promise<boolean> => {
    return new Promise((resolve) => {
      setModalConfig(config);
      setIsOpen(true);
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(true);
      resolveRef.current = null;
    }
    setIsOpen(false);
  }, []);

  const handleCancel = useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    modalConfig,
    confirm,
    handleConfirm,
    handleCancel,
  };
};

// Props for the confirmation modal component
export interface ConfirmationModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
  children?: React.ReactNode;
}

// Default confirmation modal component
export const ConfirmationModal = ({
  isOpen, onConfirm, onCancel, title = "Confirm Action", message, confirmText = "Confirm", cancelText = "Cancel",
  isDestructive = false, children,
}: ConfirmationModalProps) => {
  const getMessage = (): string => {
    return message || "Are you sure you want to proceed?";
  };

  return (
    <CabModal
      open={isOpen}
      onClose={onCancel}
      title={title}
      isAlert={true}
      noFullScreen={true}
      actionButtons={
        <>
          <CabButton buttonType="secondary" onClick={onCancel}>
            {cancelText}
          </CabButton>
          <CabButton 
            onClick={onConfirm}
            color={isDestructive ? "error" : "primary"}
          >
            {confirmText}
          </CabButton>
        </>
      }
    >
      {children || (
        <>
          <Typography>
            {getMessage()}
          </Typography>
          {isDestructive && (
            <Typography variant="body2" color={colors.black800} sx={{ marginTop: 1 }}>
              This action cannot be undone.
            </Typography>
          )}
        </>
      )}
    </CabModal>
  );
};

// Convenience hook that returns both the confirmation logic and the modal component
export const useConfirmationModalWithUI = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<ConfirmationConfig>({
    title: "Confirm Action",
    message: "Are you sure you want to proceed?",
    confirmText: "Confirm",
    cancelText: "Cancel"
  });
  const [showFinalConfirmation, setShowFinalConfirmation] = useState(false);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const handleConfirm = useCallback(() => {
    if (modalConfig.doubleConfirm && !showFinalConfirmation) {
      setShowFinalConfirmation(true);
    } else {
      if (resolveRef.current) {
        resolveRef.current(true);
        resolveRef.current = null;
      }
      setIsOpen(false);
      setShowFinalConfirmation(false);
    }
  }, [modalConfig.doubleConfirm, showFinalConfirmation]);

  const handleCancel = useCallback(() => {
    if (resolveRef.current) {
      resolveRef.current(false);
      resolveRef.current = null;
    }
    setIsOpen(false);
    setShowFinalConfirmation(false);
  }, []);

  const ConfirmationModalComponent = useCallback(() => {
    if (!isOpen && !showFinalConfirmation) {
      return null;
    }

    let config;
    if (modalConfig.doubleConfirm && showFinalConfirmation) {
      config = {
        title: modalConfig.doubleConfirm.title,
        message: modalConfig.doubleConfirm.message,
        confirmText: modalConfig.doubleConfirm.confirmText,
        cancelText: modalConfig.doubleConfirm.cancelText,
        isDestructive: modalConfig.isDestructive,
      };
    } else {
      config = {
        title: modalConfig.title,
        message: modalConfig.message,
        confirmText: modalConfig.confirmText,
        cancelText: modalConfig.cancelText,
        isDestructive: modalConfig.isDestructive,
      };
    }

    return (
      <ConfirmationModal
        isOpen={true}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title={config.title}
        message={config.message}
        confirmText={config.confirmText}
        cancelText={config.cancelText}
        isDestructive={config.isDestructive}
      />
    );
  }, [
    isOpen,
    showFinalConfirmation,
    handleConfirm,
    handleCancel,
    modalConfig
  ]);

  // Override the confirm function to handle double confirmation
  const confirm = useCallback((config: ConfirmationConfig): Promise<boolean> => {
    return new Promise((resolve) => {
      setModalConfig(config);
      setIsOpen(true);
      setShowFinalConfirmation(false);
      resolveRef.current = resolve;
    });
  }, []);

  return {
    confirm,
    ConfirmationModalComponent,
  };
}; 